export type TeeBox = 'red' | 'gold' | 'white' | 'blue' | 'black';

export type GameDurationType = 'open_ended' | 'yearly' | 'custom';

export interface Hole {
  number: number;
  par: number;
  yardage: Record<TeeBox, number>;
  handicap: number;
}

export interface Course {
  id: string;
  name: string;
  holes: Hole[];
  teeOptions: TeeBox[];
  location: string;
  imageUrl?: string;
  holesCount?: number;
  par?: number;
}

export interface BirdieRecord {
  id: string;
  userId: string;
  courseId: string;
  holeNumber: number;
  date: string;
  teeBox: TeeBox;
  scoreType: 'birdie' | 'eagle' | 'double_eagle';
  notes?: string;
  gameIds: string[]; // Changed to array to track birdies across multiple games
}

export interface User {
  id: string;
  name: string;
  email?: string | null;
  imageUrl?: string | null;
  isManual?: boolean;
  isSample?: boolean;
  manualPlayerId?: string;
}

export interface BirdieGameGroup {
  id: string;
  name: string;
  courseId: string;
  courseName?: string;
  year: number;
  startDate?: string;
  endDate?: string | null;
  durationType?: GameDurationType;
  members: User[];
  bet?: string;
  winner?: string;
  isPaid?: boolean;
  isArchived?: boolean;
  isLoneWolf?: boolean;
  recurringGroupId?: string | null;
  isRecurring?: boolean;
  isSample?: boolean;
}

export interface RecurringGameGroup {
  recurringGroupId: string;
  name: string;
  instances: BirdieGameGroup[];
  currentYearInstance?: BirdieGameGroup;
}

export interface CurrentRound {
  golfers: User[];
  courseId: string;
  date: string;
  eligibleGames: BirdieGameGroup[];
}

export type FriendshipStatus = 'pending' | 'accepted';

export interface FriendWithStatus extends User {
  friendshipId?: string;
  status?: FriendshipStatus;
  isRequester?: boolean;
}

export interface DbCourse {
  id: string;
  name: string;
  state: string;
  city: string;
  user_id: string;
  available_tees: TeeBox[];
  created_at: string;
  updated_at: string;
  holes_count?: number;
  par?: number;
  image_url?: string;
  api_course_id?: number | null;
}

export interface DbHole {
  id: string;
  course_id: string;
  hole_number: number;
  par: number;
  handicap?: number;
  created_at: string;
  updated_at: string;
}

export interface DbGame {
  id: string;
  name: string;
  course_id: string;
  date_created: string;
  start_date: string;
  end_date: string | null;
  duration_type: GameDurationType;
  eligible_tees: TeeBox[];
  game_type: string;
  is_lone_wolf: boolean;
  is_recurring: boolean;
  recurring_group_id: string | null;
  bet: string | null;
  created_at: string;
  updated_at: string;
}

export interface DbGamePlayer {
  id: string;
  game_id: string;
  player_id: string;
  created_at: string;
}

export interface DbScore {
  id: string;
  entry_date: string;
  entered_by_user_id: string;
  user_id: string;
  course_id: string;
  tee: TeeBox;
  score_type: 'birdie' | 'eagle' | 'double_eagle';
  score_date: string;
  hole_number: number;
  notes: string | null;
  manual_player_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ManualPlayer {
  id: string;
  name: string;
  email: string | null;
  createdBy: string;
  createdAt: string;
}

/** Prefix used on User.id for manual players to avoid UUID collisions with real profiles */
export const MANUAL_PLAYER_PREFIX = 'manual_';

/** Extract the raw manual_players.id from a prefixed User.id */
export const getManualPlayerId = (userId: string): string | null =>
  userId.startsWith(MANUAL_PLAYER_PREFIX) ? userId.slice(MANUAL_PLAYER_PREFIX.length) : null;

/** Create a prefixed User.id from a manual_players.id */
export const toManualUserId = (manualPlayerId: string): string =>
  `${MANUAL_PLAYER_PREFIX}${manualPlayerId}`;

/** Check if a User.id represents a manual player */
export const isManualUserId = (userId: string): boolean =>
  userId.startsWith(MANUAL_PLAYER_PREFIX);
